/**
 * Análises › Exportações (D532; doc 20 §8 row E3): the pure half of the screen that lists the asynchronous exports, their schedules and
 * templates. Nothing here touches the network or the DOM: the words of a status and of a failure (as copy ids), the polling rule, the column
 * choice (check, order), the schedule form and its validation, and the link an e-mail carries. The server is the authority on every rule
 * (who may export, over which markets, who may receive); this only keeps the screen honest and readable.
 */
import { type AnalyticsExportCadence, type AnalyticsExportColumn, type AnalyticsExportJob, type AnalyticsExportSchedule, type AnalyticsExportStatus, type ApiError } from '@vello/api-client';
import { biErrorView } from './bi-errors.js';

/** How often an open export is looked at again, while the screen is open and the page visible. */
export const EXPORT_POLL_MS = 3_000;

export const isOpenJob = (job: Pick<AnalyticsExportJob, 'status'>): boolean => job.status === 'queued' || job.status === 'running';
export const needsPolling = (jobs: readonly Pick<AnalyticsExportJob, 'status'>[]): boolean => jobs.some(isOpenJob);

export type StatusTone = 'neutral' | 'success' | 'warning' | 'danger';

const STATUS: Readonly<Record<AnalyticsExportStatus, { readonly messageId: string; readonly tone: StatusTone }>> = {
  queued: { messageId: 'bi.exp.status.queued', tone: 'neutral' },
  running: { messageId: 'bi.exp.status.running', tone: 'neutral' },
  done: { messageId: 'bi.exp.status.done', tone: 'success' },
  failed: { messageId: 'bi.exp.status.failed', tone: 'danger' },
  expired: { messageId: 'bi.exp.status.expired', tone: 'neutral' },
  cancelled: { messageId: 'bi.exp.status.cancelled', tone: 'warning' },
};

export const statusView = (status: AnalyticsExportStatus): { readonly messageId: string; readonly tone: StatusTone } => STATUS[status];

/** The reason codes a job (or a schedule's last run) can carry, each with a plain sentence; anything else reads as the generic one. */
export const EXPORT_REASON_CODES: readonly string[] = [
  'EXPORT_NOT_AUTHORIZED', 'EXPORT_PERSONAL_NOT_AUTHORIZED', 'EXPORT_SCOPE_CHANGED', 'EXPORT_REQUEST_INVALID', 'EXPORT_ANALYTICS_UNAVAILABLE', 'EXPORT_QUERY_TIMEOUT',
  'EXPORT_DEADLINE_EXCEEDED', 'EXPORT_TOO_LARGE', 'EXPORT_FILE_TOO_LARGE', 'EXPORT_AUDIT_UNAVAILABLE', 'EXPORT_CANCELLED', 'EXPORT_INTERRUPTED', 'EXPORT_FAILED',
  'SCHEDULE_SLOT_STALE', 'NO_AUTHORIZED_RECIPIENT', 'EXPORT_NOT_AVAILABLE', 'RECIPIENT_NOT_AUTHORIZED', 'RECIPIENT_PERSONAL_NOT_AUTHORIZED', 'RECIPIENT_NO_ADDRESS',
  'MAIL_VAULT_UNAVAILABLE', 'MAIL_NOT_CONFIGURED', 'MAIL_DISABLED', 'MAIL_SEND_FAILED', 'DELIVERY_LINK_BASE_MISSING', 'DELIVERY_AUDIT_UNAVAILABLE',
];
const REASONS: ReadonlySet<string> = new Set(EXPORT_REASON_CODES);

/** The copy id of a reason code (`bi.exp.reason.<CODE>`), or the generic one for a code this screen has no sentence for. */
export const reasonMessageId = (code: string | undefined): string | undefined => (code === undefined ? undefined : REASONS.has(code) ? `bi.exp.reason.${code}` : 'bi.exp.reason.OTHER');

export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 ? Math.round(value) : Math.round(value * 10) / 10} ${units[unit]}`;
}

/** What a job's request says, as ids a screen can name: the metrics and the cuts of a query, or the catalogue report. Never a value. */
export function describeRequest(job: Pick<AnalyticsExportJob, 'source' | 'request'>): { readonly report?: string; readonly list?: string; readonly metrics: readonly string[]; readonly dimensions: readonly string[] } {
  const r = job.request as { metrics?: unknown; dimensions?: unknown; reportId?: unknown; list?: unknown };
  if (job.source.kind === 'CATALOG_REPORT') return { report: typeof r.reportId === 'string' ? r.reportId : job.source.ref ?? '', metrics: [], dimensions: [] };
  // D594: an Admin list: its id (the vocabulary `analytics.list_id` names it), never a filter value.
  if (job.source.kind === 'LIST_REPORT') return { list: typeof r.list === 'string' ? r.list : job.source.ref ?? '', metrics: [], dimensions: [] };
  const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  return { metrics: list(r.metrics), dimensions: list(r.dimensions) };
}

// ───────────────────────────── failures ─────────────────────────────

export interface ExportErrorView {
  readonly messageId: string;
  readonly retryable: boolean;
  readonly reference: string;
}

const BY_REASON: Readonly<Record<string, string>> = {
  EXPORT_LIMIT_REACHED: 'bi.exp.err.limit',
  ANALYTICS_EXPORT_NOT_READY: 'bi.exp.err.notReady',
  ANALYTICS_EXPORT_EXPIRED: 'bi.exp.err.expired',
  ANALYTICS_EXPORT_NOT_FOUND: 'bi.exp.err.notFound',
  ANALYTICS_EXPORT_NOT_CANCELLABLE: 'bi.exp.err.notCancellable',
  ANALYTICS_EXPORT_AUDIT_UNAVAILABLE: 'bi.exp.err.audit',
  ANALYTICS_ACCESS_LOG_UNAVAILABLE: 'bi.exp.err.accessLog',
  // On an export the access words are about what the operator holds NOW over a file or a source, not about a cut to remove from a query.
  ANALYTICS_PERSONAL_FORBIDDEN: 'bi.exp.err.personalLost',
  ANALYTICS_LINKAGE_FORBIDDEN: 'bi.exp.err.personalLost',
  ANALYTICS_MARKET_FORBIDDEN: 'bi.exp.err.marketLost',
  EXPORT_COLUMN_UNKNOWN: 'bi.exp.err.column',
  EXPORT_COLUMN_DUPLICATED: 'bi.exp.err.column',
  EXPORT_COLUMNS_INVALID: 'bi.exp.err.column',
  EXPORT_COLUMNS_TOO_MANY: 'bi.exp.err.column',
  EXPORT_SOURCE_INVALID: 'bi.exp.err.source',
  // D594: an Admin list as the source - the period, the markets and the list's own filters are checked as on the page.
  LIST_UNKNOWN: 'bi.exp.err.source',
  LIST_FILTER_INVALID: 'bi.exp.err.source',
  LIST_PART_INVALID: 'bi.exp.err.source',
  EXPORT_REQUEST_TOO_LARGE: 'bi.exp.err.source',
  LIST_RANGE_INVALID: 'bi.exp.err.listPeriod',
  LIST_RANGE_TOO_LONG: 'bi.exp.err.listPeriod',
  LIST_MARKET_INVALID: 'bi.exp.err.listMarkets',
  LIST_MARKET_REQUIRED: 'bi.exp.err.listMarkets',
  REPORT_PERSONAL_VALUES_FORBIDDEN: 'bi.exp.err.personalValues',
  SCHEDULE_NAME_TAKEN: 'bi.exp.err.scheduleName',
  SCHEDULE_NAME_INVALID: 'bi.exp.err.scheduleName',
  SCHEDULE_LIMIT_REACHED: 'bi.exp.err.scheduleLimit',
  SCHEDULE_CADENCE_INVALID: 'bi.exp.err.cadence',
  SCHEDULE_ZONE_INVALID: 'bi.exp.err.zone',
  SCHEDULE_RECIPIENTS_INVALID: 'bi.exp.err.recipients',
  SCHEDULE_RECIPIENT_INVALID: 'bi.exp.err.recipientInvalid',
  SCHEDULE_RECIPIENT_NOT_AUTHORIZED: 'bi.exp.err.recipientNotAuthorized',
  TEMPLATE_NAME_TAKEN: 'bi.exp.err.templateName',
  TEMPLATE_NAME_INVALID: 'bi.exp.err.templateName',
  TEMPLATE_LIMIT_REACHED: 'bi.exp.err.templateLimit',
  FILTER_SET_SHARE_NEEDS_MARKET: 'bi.exp.err.shareNeedsMarket',
};

/** What a refused or failed export call is called on screen: a plain sentence per reason code, the Análises words for everything else. */
export function exportErrorView(error: Pick<ApiError, 'code' | 'status' | 'reasonCode' | 'retryAfterSeconds'>): ExportErrorView {
  const own = error.reasonCode !== undefined ? BY_REASON[error.reasonCode] : undefined;
  if (own !== undefined) return { messageId: own, retryable: false, reference: error.reasonCode as string };
  const generic = biErrorView(error);
  return { messageId: generic.messageId, retryable: generic.retryable, reference: generic.reference };
}

// ───────────────────────────── the column choice ─────────────────────────────

/** The columns checked, in the order the file will have them. `undefined` = every column, in the catalog's order. */
export type ExportColumnChoice = readonly string[];

/** Check or uncheck a column: a checked column joins at the end, an unchecked one leaves; the catalog's order is the starting order. */
export function toggleExportColumn(choice: ExportColumnChoice, id: string, on: boolean): ExportColumnChoice {
  const has = choice.includes(id);
  if (on) return has ? choice : [...choice, id];
  return has ? choice.filter((c) => c !== id) : choice;
}

/** Move a checked column one place earlier (-1) or later (+1) in the file. */
export function moveExportColumn(choice: ExportColumnChoice, id: string, delta: -1 | 1): ExportColumnChoice {
  const at = choice.indexOf(id);
  const to = at + delta;
  if (at < 0 || to < 0 || to >= choice.length) return choice;
  const next = [...choice];
  next.splice(at, 1);
  next.splice(to, 0, id);
  return next;
}

/** What to send: `[]` (every column) when the choice is the whole catalog in its own order, the list itself otherwise. */
export function columnsToSend(choice: ExportColumnChoice, all: readonly Pick<AnalyticsExportColumn, 'id'>[]): readonly string[] {
  return choice.length === all.length && choice.every((id, i) => id === all[i]?.id) ? [] : choice;
}

/** The starting choice: what a template carries, or every column. */
export function initialExportChoice(all: readonly Pick<AnalyticsExportColumn, 'id'>[], preset: readonly string[]): ExportColumnChoice {
  const known = new Set(all.map((c) => c.id));
  const kept = preset.filter((id) => known.has(id));
  return kept.length > 0 ? kept : all.map((c) => c.id);
}

// ───────────────────────────── the schedule form ─────────────────────────────

export interface ScheduleDraft {
  readonly name: string;
  readonly kind: AnalyticsExportCadence['kind'];
  /** 1 = Monday ... 7 = Sunday. */
  readonly weekday: number;
  readonly day: number;
  /** `HH:MM`, the local time in the zone of `zoneMarket`. */
  readonly time: string;
  readonly zoneMarket: string;
  readonly recipients: readonly string[];
  readonly enabled: boolean;
}

export const emptyDraft = (zoneMarket: string, recipient?: string): ScheduleDraft => ({
  name: '',
  kind: 'WEEKLY',
  weekday: 1,
  day: 1,
  time: '08:00',
  zoneMarket,
  recipients: recipient !== undefined ? [recipient] : [],
  enabled: true,
});

const pad = (n: number): string => String(n).padStart(2, '0');

export function draftOfSchedule(s: Pick<AnalyticsExportSchedule, 'name' | 'cadence' | 'zoneMarket' | 'recipients' | 'enabled'>): ScheduleDraft {
  const c = s.cadence;
  return {
    name: s.name,
    kind: c.kind,
    weekday: c.kind === 'WEEKLY' ? c.weekday : 1,
    day: c.kind === 'MONTHLY' ? c.day : 1,
    time: `${pad(c.hour)}:${pad(c.minute)}`,
    zoneMarket: s.zoneMarket,
    recipients: s.recipients,
    enabled: s.enabled,
  };
}

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function cadenceOfDraft(d: ScheduleDraft): AnalyticsExportCadence | undefined {
  const m = TIME.exec(d.time);
  if (m === null) return undefined;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (d.kind === 'DAILY') return { kind: 'DAILY', hour, minute };
  if (d.kind === 'WEEKLY') return d.weekday >= 1 && d.weekday <= 7 ? { kind: 'WEEKLY', weekday: d.weekday, hour, minute } : undefined;
  return d.day >= 1 && d.day <= 31 ? { kind: 'MONTHLY', day: d.day, hour, minute } : undefined;
}

export type DraftCheck = { readonly ok: true } | { readonly ok: false; readonly field: 'name' | 'time' | 'day' | 'zone' | 'recipients'; readonly messageId: string };

export function checkDraft(d: ScheduleDraft): DraftCheck {
  if (d.name.trim() === '' || d.name.trim().length > 80) return { ok: false, field: 'name', messageId: 'bi.exp.sch.err.name' };
  if (!TIME.test(d.time)) return { ok: false, field: 'time', messageId: 'bi.exp.sch.err.time' };
  if (d.kind === 'MONTHLY' && !(Number.isInteger(d.day) && d.day >= 1 && d.day <= 31)) return { ok: false, field: 'day', messageId: 'bi.exp.sch.err.day' };
  if (d.zoneMarket === '') return { ok: false, field: 'zone', messageId: 'bi.exp.sch.err.zone' };
  if (d.recipients.length === 0) return { ok: false, field: 'recipients', messageId: 'bi.exp.sch.err.recipients' };
  return { ok: true };
}

export const toggleRecipient = (list: readonly string[], id: string, on: boolean): readonly string[] => (on ? (list.includes(id) ? list : [...list, id]) : list.filter((r) => r !== id));

// ───────────────────────────── the e-mail's link ─────────────────────────────

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The job a delivery e-mail points at (`?job=<id>`), or `undefined`. A link grants nothing: it only scrolls to a row the server lets the operator see. */
export function jobFromLink(search: string): string | undefined {
  try {
    const id = new URLSearchParams(search).get('job');
    return id !== null && UUID.test(id) ? id.toLowerCase() : undefined;
  } catch {
    return undefined;
  }
}
